import { Text } from 'src/modules/shared/components/atoms/text';
import {
  type CustomFieldFileMetadata,
  CustomFieldType,
} from 'src/api/generated';
import { formatCustomFieldValue } from 'src/modules/shared/utils/custom-fields.util';
import { formatLegacyAwareCustomFieldDateDisplayValue } from 'src/modules/shared/utils/custom-field-date-legacy.util';
import {
  tableCellText,
  tableVariantClasses,
} from 'src/modules/shared/components/molecules/table/styles';
import { FileDownloadButton } from 'src/modules/shared/components/atoms/buttons/file-download-button';
import {
  ensureUrlWithProtocol,
  isProbablyUrl,
} from 'src/modules/shared/utils/validation/url.util';
import { Link } from 'src/modules/shared/components/atoms/links/link';

interface CustomFieldTableValueProps {
  value: unknown;
  fieldType: CustomFieldType;
  includeTime?: boolean;
  variant?: keyof typeof tableVariantClasses;
}

export const CustomFieldTableValue: React.FC<CustomFieldTableValueProps> = ({
  value,
  fieldType,
  includeTime = false,
  variant = 'primary',
}) => {
  if (value === null || value === undefined) {
    return null;
  }

  const { text } = tableVariantClasses[variant];

  if (fieldType === CustomFieldType.BOOLEAN) {
    const boolValue = Boolean(value);
    return (
      <div className="flex items-center">
        <span
          className={`flex h-4 w-4 rounded-full ${
            boolValue ? 'bg-success-500' : 'bg-secondary-300'
          }`}
        />
        <Text size="small" className={`${tableCellText} ${text} ml-2`}>
          {boolValue ? 'Sí' : 'No'}
        </Text>
      </div>
    );
  }

  if (fieldType === CustomFieldType.DATE) {
    return (
      <Text size="small" className={`${tableCellText} ${text}`}>
        {formatLegacyAwareCustomFieldDateDisplayValue(value, includeTime)}
      </Text>
    );
  }

  if (fieldType === CustomFieldType.FILE) {
    const fileMetadata = value as CustomFieldFileMetadata;

    if (fileMetadata.fileId && fileMetadata.filename) {
      return (
        <FileDownloadButton
          fileId={fileMetadata.fileId}
          size="xs"
          variant={variant}
          appearance="soft"
          filename={fileMetadata.filename}
          fileSize={fileMetadata.size}
        />
      );
    }
  }

  const asString = formatCustomFieldValue(value, fieldType);
  const looksLikeUrl = isProbablyUrl(asString);

  if (looksLikeUrl) {
    const href = ensureUrlWithProtocol(asString);
    const displayText =
      asString.length > 30 ? `${asString.substring(0, 30)}...` : asString;
    return (
      <Link href={href} size="small" className={`${tableCellText} ${text}`}>
        {displayText}
      </Link>
    );
  }

  return (
    <Text size="small" className={`${tableCellText} ${text}`}>
      {asString}
    </Text>
  );
};
